import { Document, Page, Text, View, StyleSheet, Font, Image } from '@react-pdf/renderer';

Font.register({
  family: 'Helvetica',
  fonts: [
    { src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-light-webfont.ttf', fontWeight: 300 },
    { src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-regular-webfont.ttf', fontWeight: 400 },
    { src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-medium-webfont.ttf', fontWeight: 500 },
    { src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-bold-webfont.ttf', fontWeight: 700 },
  ]
});

const getAnswerMeaning = (answer, scale) => {
  if (!scale || answer === "Not answered") return answer;

  if (scale.buttons) {
    const button = scale.buttons.find(btn => btn.buttonValue === answer);
    if (button) return button.meaning || answer;
  }

  if (scale.notApplicableButton && scale.notApplicableButton.buttonValue === answer) {
    return scale.notApplicableButton.buttonText || answer;
  }

  return answer;
};

const SurveyResponsePDF = ({ surveyData, processedSections, info, logos, translatedText }) => {
  const surveyName = surveyData.surveyDocument?.survey?.name || "Rater Assessment Results";

  return (
    <Document>
      <Page size="LETTER" style={styles.page}>
          <View style={styles.topLeftLogo}>
            <Image src={logos.topLeft} style={styles.logo} />
          </View>
          
          <View style={styles.bottomRightLogo} fixed>
            <Image src={logos.bottomRight} style={styles.logo} />
          </View>
        <View style={styles.header}>
          <Text style={styles.title}>{surveyName}</Text>
          <Text style={styles.subtitle}>{translatedText[0]}</Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>{translatedText[1]}</Text>
          <Text style={styles.text}>
            { translatedText[2].replace("{{firstName}}", info.participant.firstName)
                .replace("{{lastName}}", info.participant.lastName)
                .replace("{{raterRole}}", info.rater.raterType.name)
            }
          </Text>
        </View>

        {processedSections.map((section, sectionIndex) => (
          <View key={sectionIndex} break={sectionIndex > 0}>
            <Text style={styles.sectionTitle}>{section.heading}</Text>
            {section.instructions && (
              <Text style={styles.text}>
                {section.instructions.join(' ')}
              </Text>
            )}

            {section.questionGroups.map((group, groupIndex) => (
              <View key={groupIndex}>
                {group.questions.map((question, qIndex) => (
                  <View key={qIndex} style={styles.response}>
                    <Text style={styles.question}>
                      {question.number && question.number !== '0' ? `${question.number}. ` : ''}{question.text}
                    </Text>
                    <Text style={styles.answer}>
                      Response: {getAnswerMeaning(question.answer, question.scale)}
                    </Text>
                    {question.dependentOption?.answer && (
                      <Text style={styles.answer}>
                        {question.dependentOption.answer}
                      </Text>
                    )}
                    {question.options?.filter(opt => opt.formItemId).map((option, oIndex) => (
                      <View key={oIndex}>
                        {option.answer && (
                          <Text style={styles.answer}>
                            {option.text}: {option.answer}
                          </Text>
                        )}
                        {option.dependentOption?.answer && (
                          <Text style={styles.answer}>
                            {option.dependentOption.answer}
                          </Text>
                        )}
                      </View>
                    ))}
                  </View>
                ))}
              </View>
            ))}
          </View>
        ))}

        <Text style={styles.pageNumber} render={({ pageNumber, totalPages }) => (
          translatedText[3].replace("{{pageNumber}}",pageNumber+'').replace("{{totalPages}}", totalPages+'')
        )} fixed />
      </Page>
    </Document>
  );
};

const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    backgroundColor: "#FFFFFF",
    paddingTop: 30,
    paddingLeft: 30,
    paddingRight: 30,
    paddingBottom: 60,
    fontFamily: 'Helvetica',
  },
  logo: {
    width: '100%'
  },
  topLeftLogo: {
    position: 'absolute',
    top: 0,
    left: 30,
    width: 90,
    height: 90
  },
  bottomRightLogo: {
  position: 'absolute',
  bottom: 20,
  right: 20,
  width: 100, 
  },
  header: {
    marginBottom: 20,
    textAlign: "center",
    borderBottom: "1pt solid #e0e0e0",
    paddingBottom: 10,
  },
  title: {
    fontSize: 22,
    marginBottom: 10,
    fontWeight: 700,
    color: "#2c3e50",
  },
  subtitle: {
    fontSize: 14,
    marginBottom: 5,
    color: "#7f8c8d",
    fontWeight: 400,
  },
  section: {
    marginBottom: 15,
  },
  sectionTitle: {
    fontSize: 16,
    marginBottom: 8,
    fontWeight: 700,
    color: "#34495e",
    backgroundColor: "#f8f9fa",
    padding: 5,
    marginTop: 10,
  },
  text: {
    fontSize: 12,
    marginBottom: 5,
    lineHeight: 1.4,
    fontWeight: 400,
  },
  response: {
    fontSize: 12,
    marginBottom: 8,
    paddingLeft: 10,
    color: "#2c3e50",
    lineHeight: 1.4,
  },
  question: {
    fontSize: 12,
    marginBottom: 3,
    fontWeight: 500,
  },
  answer: {
    fontSize: 12,
    color: "#3498db",
    fontWeight: 400,
  },
  footer: {
    position: "absolute",
    bottom: 30,
    left: 30,
    right: 30,
    textAlign: "center",
    fontSize: 10,
    color: "#95a5a6",
    borderTop: "1pt solid #e0e0e0",
    paddingTop: 10,
  },
  scaleInfo: {
    fontSize: 10,
    color: "#7f8c8d",
    fontStyle: 'italic',
    marginBottom: 5,
  },
  pageNumber: {
    position: 'absolute',
    fontSize: 10,
    bottom: 20,
    left: 0,
    right: 0,
    textAlign: 'center',
    color: '#95a5a6',
  }
});

export default SurveyResponsePDF;